import React, { useEffect, useMemo, useState } from "react";
import {
  getAllEvents,
  getEventRegistrations,
} from "./../../../services/eventServices";
import {
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaSyncAlt,
  FaUsers,
  FaClipboardCheck,
} from "react-icons/fa";
import QRCode from "react-qr-code";

/* =========================
   Helpers dates & filtres
   ========================= */
const TYPE_CN = "Cours Centre National";

const fmtDate = (d) =>
  d.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const startOfWeekMon = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0..6 (0=Dim)
  const diff = (day === 0 ? -6 : 1) - day; // ramener au lundi
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};
const endOfWeekSun = (date) => {
  const start = startOfWeekMon(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const eventCoversDate = (ev, day) => {
  const s = ev?.start ? new Date(ev.start) : null;
  const e = ev?.end ? new Date(ev.end) : null;
  if (!s) return false;
  if (!e) return sameDay(s, day);
  const d0 = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const d1 = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  const dx = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  return dx >= d0 && dx <= d1;
};

/* =========================
   Page
   ========================= */
export default function InscriptionsCN() {
  // Semaine affichée
  const [anchorDate, setAnchorDate] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  });
  const weekStart = useMemo(() => startOfWeekMon(anchorDate), [anchorDate]);
  const weekEnd = useMemo(() => endOfWeekSun(anchorDate), [anchorDate]);

  // Événements CN de la semaine
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [showQr, setShowQr] = useState(false);

  const QR_URL = useMemo(() => {
    const base = (process.env.REACT_APP_URL || "").replace(/\/+$/, "");
    return `${base}/inscriptions-events`;
  }, []);

  // Jour sélectionné & événement sélectionné
  const [selectedDay, setSelectedDay] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  });
  const [selectedEventId, setSelectedEventId] = useState(null);

  // Inscriptions
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);

  // Recherche
  const [query, setQuery] = useState("");

  // Charger événements (tous), puis filtrer côté front
  useEffect(() => {
    (async () => {
      setLoadingEvents(true);
      try {
        const data = await getAllEvents();
        const list = Array.isArray(data) ? data : [];
        // Filtrer CN uniquement
        const cnOnly = list.filter(
          (e) => e?.type_event === TYPE_CN && e.is_active
        );
        // Tri par date
        cnOnly.sort((a, b) => new Date(a.start) - new Date(b.start));
        setEvents(cnOnly);
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, []);

  // Génère les 7 jours de la semaine affichée
  const weekDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  // Événements CN de la semaine (filtrés par dates de début dans la semaine)
  const weekEvents = useMemo(() => {
    return events.filter((e) => {
      const s = e?.start ? new Date(e.start) : null;
      if (!s) return false;
      return s >= weekStart && s <= weekEnd;
    });
  }, [events, weekStart, weekEnd]);

  // 👉 Jours de la semaine qui ont AU MOINS un cours
  const weekDaysWithEvents = useMemo(() => {
    return weekDays.filter((d) => weekEvents.some((e) => eventCoversDate(e, d)));
  }, [weekDays, weekEvents]);

  // Événements pour le jour sélectionné
  const dayEvents = useMemo(() => {
    return weekEvents.filter((e) => eventCoversDate(e, selectedDay));
  }, [weekEvents, selectedDay]);

  // À chaque changement de semaine → si le jour sélectionné sort de la semaine, le recaler
  useEffect(() => {
    if (selectedDay < weekStart || selectedDay > weekEnd) {
      // si des jours avec évènements existent, se caler sur le 1er
      if (weekDaysWithEvents.length > 0) {
        setSelectedDay(weekDaysWithEvents[0]);
      } else {
        setSelectedDay(weekStart);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, weekEnd, weekDaysWithEvents]);

  // Si le jour sélectionné n'a pas d'événement mais qu’il existe des jours valides, se caler sur le 1er
  useEffect(() => {
    if (
      weekDaysWithEvents.length > 0 &&
      !weekDaysWithEvents.some((d) => sameDay(d, selectedDay))
    ) {
      setSelectedDay(weekDaysWithEvents[0]);
    }
  }, [weekDaysWithEvents, selectedDay]);

  // Choix automatique d’un événement : priorité au jour sélectionné, sinon premier de la semaine
  useEffect(() => {
    if (dayEvents.length > 0) {
      setSelectedEventId((prev) => {
        if (prev && dayEvents.some((e) => Number(e.id) === Number(prev)))
          return prev;
        return dayEvents[0].id;
      });
    } else if (weekEvents.length > 0) {
      setSelectedEventId(weekEvents[0].id);
      setSelectedDay(new Date(weekEvents[0].start));
    } else {
      setSelectedEventId(null);
    }
  }, [dayEvents, weekEvents]);

  // Charger inscriptions à chaque changement d’événement
  useEffect(() => {
    if (!selectedEventId) {
      setRegistrations([]);
      return;
    }
    (async () => {
      setLoadingRegs(true);
      try {
        const data = await getEventRegistrations(Number(selectedEventId));
        setRegistrations(Array.isArray(data) ? data : []);
      } finally {
        setLoadingRegs(false);
      }
    })();
  }, [selectedEventId]);

  // Normalisation inscriptions (sans saison)
  const normalizedRegs = useMemo(() => {
    return registrations.map((r) => {
      const L = r?.Licencie || r?.licencie || r;
      return {
        licencie_id: L?.id ?? r?.licencie_id ?? null,
        nom: L?.nom ?? r?.nom ?? "",
        prenom: L?.prenom ?? r?.prenom ?? "",
        club: L?.club ?? r?.club ?? "",
        licence_number: L?.licence_number ?? r?.licence_number ?? "",
        genre: L?.genre ?? r?.genre ?? "",
      };
    });
  }, [registrations]);

  // Recherche
  const filteredRegs = useMemo(() => {
    const q = normalize(query);
    if (!q) return normalizedRegs;
    return normalizedRegs.filter((x) => {
      const hay = normalize(
        [x.nom, x.prenom, x.club, x.licence_number].filter(Boolean).join(" ")
      );
      return hay.includes(q);
    });
  }, [normalizedRegs, query]);

  // Info event sélectionné
  const selectedEvent = useMemo(
    () =>
      selectedEventId
        ? events.find((e) => Number(e.id) === Number(selectedEventId))
        : null,
    [events, selectedEventId]
  );

  // Comptage
  const total = filteredRegs.length;

  // UI handlers
  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setAnchorDate(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setAnchorDate(d);
  };
  const todayWeek = () => setAnchorDate(new Date());

  return (
    <div className="w-full pt-[90px]">
      {/* Header sticky (mobile friendly) */}
      <div className="sticky top-[64px] z-30 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white shadow">
        <div className="max-w-[1100px] mx-auto px-4 py-3 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <FaClipboardCheck /> Inscriptions — {TYPE_CN}
            </h1>
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={prevWeek}
                className="rounded-lg bg-white/10 hover:bg-white/20 px-2 py-1"
                title="Semaine précédente"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={todayWeek}
                className="rounded-lg bg-white/10 hover:bg-white/20 px-2 py-1"
                title="Cette semaine"
              >
                Aujourd’hui
              </button>
              <button
                onClick={nextWeek}
                className="rounded-lg bg-white/10 hover:bg-white/20 px-2 py-1"
                title="Semaine suivante"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          {/* Bandeau semaine (scrollable en mobile) — n’affiche que les jours avec cours */}
          <div className="flex items-center gap-2 overflow-x-auto py-1">
            <button
              onClick={prevWeek}
              className="sm:hidden shrink-0 rounded-lg bg-white/10 hover:bg-white/20 px-2 py-1"
              title="Semaine précédente"
            >
              <FaChevronLeft />
            </button>

            {weekDaysWithEvents.length === 0 ? (
              <div className="text-sm opacity-90">
                Aucun cours « {TYPE_CN} » cette semaine.
              </div>
            ) : (
              weekDaysWithEvents.map((d) => {
                const isActive = sameDay(d, selectedDay);
                const count = weekEvents.filter((e) =>
                  eventCoversDate(e, d)
                ).length;
                return (
                  <button
                    key={d.toISOString()}
                    onClick={() => setSelectedDay(d)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-sm ring-1 transition
                    ${
                      isActive
                        ? "bg-white text-indigo-700 ring-white"
                        : "bg-white/10 ring-white/30 text-white hover:bg-white/20"
                    }`}
                    title={d.toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  >
                    {fmtDate(d)} {count > 0 ? `· ${count} cours` : ""}
                  </button>
                );
              })
            )}

            <button
              onClick={nextWeek}
              className="sm:hidden shrink-0 rounded-lg bg-white/10 hover:bg-white/20 px-2 py-1"
              title="Semaine suivante"
            >
              <FaChevronRight />
            </button>
          </div>

          <button
            onClick={() => setShowQr(true)}
            className="rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"
            title="Afficher le QR code d’inscription"
          >
            QR Code inscription
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-[1100px] mx-auto px-4 py-4 grid gap-4 sm:grid-cols-3">
        {/* Col gauche : liste d’événements du jour */}
        <section className="sm:col-span-1">
          <div className="rounded-2xl border bg-white dark:bg-neutral-800 shadow">
            <div className="p-3 border-b">
              <div className="text-sm font-semibold">Cours du jour</div>
              <div className="text-xs opacity-70">
                {selectedDay.toLocaleDateString("fr-FR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
              </div>
            </div>

            <div className="max-h-[380px] overflow-y-auto">
              {loadingEvents ? (
                <div className="p-3 text-sm opacity-70">
                  Chargement des cours…
                </div>
              ) : dayEvents.length === 0 ? (
                <div className="p-3 text-sm opacity-70">
                  Aucun cours « {TYPE_CN} » ce jour.
                </div>
              ) : (
                <ul className="divide-y">
                  {dayEvents.map((ev) => {
                    const active = Number(selectedEventId) === Number(ev.id);
                    const s = new Date(ev.start);
                    const e = ev.end ? new Date(ev.end) : null;
                    const dateLabel = e
                      ? `${s.toLocaleDateString(
                          "fr-FR"
                        )} → ${e.toLocaleDateString("fr-FR")}`
                      : s.toLocaleDateString("fr-FR");

                    return (
                      <li
                        key={ev.id}
                        onClick={() => setSelectedEventId(ev.id)}
                        className={`p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-neutral-700 ${
                          active ? "bg-indigo-50 dark:bg-neutral-700/60" : ""
                        }`}
                        title={ev.titre}
                      >
                        <div className="font-medium line-clamp-1">
                          {ev.titre || "Cours"}
                        </div>
                        <div className="text-xs opacity-80">
                          {dateLabel} — {ev.lieu_event || ev.organisateur || "—"}
                        </div>
                        <div className="text-xs opacity-70">
                          {ev.description || "—"}
                        </div>
                        <div className="text-xs opacity-70">
                          {ev.horaire_event || "—"}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Contrôles semaine (mobile) */}
            <div className="p-3 border-t flex sm:hidden items-center justify-between">
              <button
                onClick={prevWeek}
                className="rounded-lg border px-3 py-1.5 text-sm"
              >
                Semaine -1
              </button>
              <button
                onClick={todayWeek}
                className="rounded-lg border px-3 py-1.5 text-sm"
              >
                Cette semaine
              </button>
              <button
                onClick={nextWeek}
                className="rounded-lg border px-3 py-1.5 text-sm"
              >
                Semaine +1
              </button>
            </div>
          </div>
        </section>

        {/* Col droite : liste des inscrits */}
        <section className="sm:col-span-2">
          <div className="rounded-2xl border bg-white dark:bg-neutral-800 shadow">
            <div className="p-3 border-b flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <FaUsers /> Liste des inscrits
                </div>
                {selectedEventId && (
                  <div className="text-xs opacity-70 mt-0.5">
                    {selectedEvent?.titre} —{" "}
                    {selectedEvent?.lieu_event ||
                      selectedEvent?.organisateur ||
                      "—"}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-white dark:bg-neutral-900">
                  <FaSearch className="opacity-70" />
                  <input
                    className="w-40 sm:w-64 bg-transparent outline-none text-sm"
                    placeholder="Recherche (nom, club, licence)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {query && (
                    <button
                      type="button"
                      className="text-xs opacity-70 hover:opacity-100"
                      onClick={() => setQuery("")}
                      title="Effacer"
                    >
                      <FaSyncAlt />
                    </button>
                  )}
                </div>

                <div className="text-xs px-3 py-2 rounded-lg bg-slate-100 dark:bg-neutral-700">
                  {loadingRegs ? "—" : `${total}`} inscrit(s)
                </div>
              </div>
            </div>

            {/* Liste inscrits */}
            <div className="p-3">
              {loadingRegs ? (
                <div className="p-3 text-sm opacity-70">
                  Chargement des inscrits…
                </div>
              ) : !selectedEventId ? (
                <div className="p-3 text-sm opacity-70">
                  Sélectionnez un cours pour afficher les inscrits.
                </div>
              ) : filteredRegs.length === 0 ? (
                <div className="p-3 text-sm opacity-70">
                  Aucun inscrit pour ce cours.
                </div>
              ) : (
                <ul className="divide-y">
                  {filteredRegs.map((x) => (
                    <li
                      key={`${x.licencie_id}-${x.licence_number}`}
                      className="py-2"
                    >
                      <div className="font-medium">
                        ✅ {x.prenom} {x.nom}
                        {x.genre ? (
                          <span className="ml-2 text-xs opacity-60">
                            ({x.genre})
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs opacity-80 truncate">
                        {x.club || "Club —"}
                      </div>
                      <div className="text-xs opacity-60">
                        {x.licence_number}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      </div>

      {showQr && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl w-[min(420px,95vw)] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">QR code — Inscriptions</h3>
              <button
                onClick={() => setShowQr(false)}
                className="rounded-lg border px-3 py-1 text-sm"
                type="button"
              >
                Fermer
              </button>
            </div>

            <div className="bg-white p-4 rounded-xl flex items-center justify-center">
              <QRCode
                value={QR_URL}
                size={256}
                style={{ width: "100%", height: "auto" }}
              />
            </div>

            <div className="mt-3 text-xs break-all select-all">{QR_URL}</div>

            <div className="mt-3 flex gap-2 justify-end">
              <button
                className="rounded-lg border px-3 py-1 text-sm"
                onClick={() => navigator.clipboard?.writeText(QR_URL)}
                type="button"
              >
                Copier le lien
              </button>
              <button
                className="rounded-lg bg-indigo-600 text-white px-3 py-1 text-sm"
                onClick={() => setShowQr(false)}
                type="button"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
